import React, { useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { useNavigate, useParams } from "react-router-dom";
import axios, { cookies, MEDIA_BASE_URL } from "../axios/axios";
import { ToastContainer, toast } from "react-toastify";

const AlbumDesignForm = () => {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [file, setFile] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("/services/getServices");
        setServices(response.data.services);
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      }
    };
    fetchServices();
  }, []);

  const newData = services.find(
    (val) => val.id.replaceAll(" ", "-").replaceAll("/", "_") === id
  );

  const onFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const qty = getQuantity();

    if (qty > 0) {
      if (selectedFiles.length > qty) {
        setFile(selectedFiles.slice(0, qty));
        toast.warn(
          `You selected more than ${qty} files — only the first ${qty} were kept.`
        );
        return;
      }

      if (selectedFiles.length < qty) {
        setFile(selectedFiles);
        toast.info(
          `You selected ${selectedFiles.length} files. Please select exactly ${qty} files before submitting.`
        );
        return;
      }

      setFile(selectedFiles);
    } else {
      setFile(selectedFiles);
      toast.info("Set quantity to enforce exact file count.");
    }
  };

  const [data, setData] = useState({
    price: "",
    unit_price: "",
    order_name: "",
    album_orientation: "",
    album_category: "",
    page_size: "",
    album_style: "",
    front_cover: "",
    back_cover: "",
    background_color: "",
    first_frame: "",
    front_image_name: "",
    front_cover_text_line1: "",
    front_cover_text_line2: "",
    back_image_name: "",
    back_cover_text_line1: "",
    back_cover_text_line2: "",
    cover_spine_text: "",
    font_options: "",
    quantity: "",
    social_media: "",
    drive_link: "",
    output_format: "",
    additional_notes: "",
  });

  useEffect(() => {
    if (newData) {
      setData((prevData) => ({
        ...prevData,
        unit_price: Number(newData.price) || 0,
        price: Number(newData.price) || 0,
        quantity: prevData.quantity || "1",
      }));
    }
  }, [newData]);

  const getQuantity = () => Math.max(0, Number(data.quantity) || 0);

  const calculateTotal = (
    qty = getQuantity(),
    unit = Number(data.unit_price) || 0
  ) => {
    const base = Number(unit) * Math.max(1, qty || 1);
    if (isCouponApplied && discount) {
      return Number((base * (1 - discount / 100)).toFixed(2));
    }
    return Number(base.toFixed(2));
  };

  const dataHandler = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Math.max(0, Math.floor(Number(value) || 0));

      setData((prevData) => {
        const unit = Number(prevData.unit_price) || 0;
        const newPrice =
          isCouponApplied && discount
            ? Number((unit * qty * (1 - discount / 100)).toFixed(2))
            : Number((unit * qty).toFixed(2));
        return { ...prevData, quantity: qty.toString(), price: newPrice };
      });

      // Trim existing selected files if they exceed new qty
      if (file && file.length > 0) {
        const selectedCount = file.length;
        if (qty === 1 && selectedCount > 1) {
          setFile([file[0]]);
          toast.info("Quantity set to 1 — extra selected files were removed.");
        } else if (qty > 0 && selectedCount > qty) {
          setFile(file.slice(0, qty));
          toast.info(
            `Quantity set to ${qty} — extra selected files were removed.`
          );
        }
      }
    } else {
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const applyCoupon = async () => {
    try {
      const storedUser = cookies.get("user");
      const userId = storedUser?.user?.id || null;

      if (!userId) {
        toast.error("User not found. Please log in again.");
        return;
      }

      const response = await axios.post("/coupon/validate", {
        code: couponCode,
        userId,
      });

      if (response.data.valid) {
        setDiscount(response.data.discount);
        setIsCouponApplied(true);

        setData((prevData) => {
          const unit = Number(prevData.unit_price) || 0;
          const qty = getQuantity() || 1;
          const total = Number(
            (unit * qty * (1 - response.data.discount / 100)).toFixed(2)
          );
          return { ...prevData, price: total };
        });

        toast.success("Coupon applied!");
      } else {
        setErrorMessage(response.data.message || "Coupon not valid");
        toast.error(response.data.message || "Coupon not valid");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setErrorMessage(
        error.response?.data?.message || "Error validating coupon."
      );
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };
  const dataSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: resData } = await axios.post(`/wedding-album/${id}`, data);

      toast.success("Data Submitted Successfully!");
      navigate(`/upload-files/${resData.id}?type=album-design`);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!newData) {
    return <h2 className="text-center my-5">Loading...</h2>;
  }

  const qty = getQuantity();
  const originalTotal = Number(data.unit_price || 0) * (qty || 1);

  const isFileCountValid = () => {
    if (qty === 0) return true;
    return file && file.length === qty;
  };
  return (
    <>
      <Navbar />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="mx-5">
        {" "}
        <div className="row align-items-center py-5 ">
          <div className="col-md-6 my-4">
            <img
              className="w-100 object-fit-cover"
              height={600}
              src={`${MEDIA_BASE_URL}/${newData.image}`}
              crossOrigin="anonymous"
              alt=""
            />
          </div>
          <div className="col-md-6 my-4 ps-5">
            <h4 className="mb-3">{newData.title}</h4>

            <h5 className="mb-3">
              Price:{" "}
              {isCouponApplied ? (
                <>
                  <del className="text-danger me-2">
                    ${originalTotal.toFixed(2)}
                  </del>
                  <span className="text-success">
                    ${Number(data.price).toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-dark">${originalTotal.toFixed(2)}</span>
              )}
            </h5>

            {/* Coupon Code Input */}
            <div className="row  g-3 align-items-center mb-1">
              <div className="col-md-4">
                <label className="fs-14 col-form-label">Coupon Code</label>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control shadow-none rounded-2 p-2"
                  placeholder="Enter Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={isCouponApplied}
                />
              </div>
              <button
                className="col-md-2 btn py-2 px-0 rounded-2 btn-secondary mt-2"
                onClick={applyCoupon}
                disabled={isCouponApplied}
              >
                Apply
              </button>
            </div>

            <form onSubmit={dataSubmit}>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Order Name
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    type="text"
                    onChange={dataHandler}
                    value={data.order_name}
                    name="order_name"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Album Orientation
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="album_orientation"
                    value={data.album_orientation}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="Square">Square</option>
                    <option value="Portrait">Portrait</option>
                    <option value="Landscape">Landscape</option>
                    <option value="Custom">Custom</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Album Category
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="album_category"
                    value={data.album_category}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="Birthday">Birthday</option>
                    <option value="Baby">Baby</option>
                    <option value="Pregnancy">Pregnancy</option>
                    <option value="Holiday">Holiday</option>
                    <option value="Travel">Travel</option>
                    <option value="Family">Family</option>
                    <option value="Other">Other</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Page Size (width x height)
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="page_size"
                    value={data.page_size}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Album Style
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="album_style"
                    value={data.album_style}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="Modren">Modren</option>
                    <option value="Traditional">Traditional</option>
                    <option value="Gallery">Gallery</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Front Cover Required
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="front_cover"
                    value={data.front_cover}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Back Cover Required
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="back_cover"
                    value={data.back_cover}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Background Color
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="background_color"
                    value={data.background_color}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="Black Only">Black Only</option>
                    <option value="White Only">White Only</option>
                    <option value="Black & White ">Black & White </option>
                    <option value="Custom ">Custom </option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    First page starts on
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="first_frame"
                    value={data.first_frame}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Name of Front Image File
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    type="text"
                    onChange={dataHandler}
                    name="front_image_name"
                    value={data.front_image_name}
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Front Cover Text - Line 1
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="front_cover_text_line1"
                    value={data.front_cover_text_line1}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Front Cover Text - Line 2
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="front_cover_text_line2"
                    value={data.front_cover_text_line2}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Name of Back Image File
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="back_image_name"
                    value={data.back_image_name}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Back Cover Text - Line 1
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="back_cover_text_line1"
                    value={data.back_cover_text_line1}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Back Cover Text - Line 2
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="back_cover_text_line2"
                    value={data.back_cover_text_line2}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Cover Spine Text
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="cover_spine_text"
                    value={data.cover_spine_text}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Font Options
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="font_options"
                    value={data.font_options}
                    type="text"
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Output Format
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="output_format"
                    value={data.output_format}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="JPEG">JPEG</option>
                    <option value="PSD in layers">PSD in layers</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Social Media
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="social_media"
                    value={data.social_media}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Quantity</label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="quantity"
                    value={data.quantity}
                    type="number"
                    min={1}
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>

              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Aditional notes
                  </label>
                </div>
                <div class="col-md-8">
                  <textarea
                    onChange={dataHandler}
                    name="additional_notes"
                    value={data.additional_notes}
                    className="form-control shadow-none rounded-2 p-2"
                  ></textarea>{" "}
                </div>
              </div>
              <button
                type="submit"
                className="btn bg_maincolor text-white"
                disabled={loading}
              >
                {loading ? "Loading..." : "Order Now"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AlbumDesignForm;
